/**
 * Comment anchors of a `.zwdoc` document (#251), for UI that only needs
 * which comments exist, in document order, and what they point at: the
 * comment margin and open-thread bookkeeping. Positions stay with the
 * editor (`CommentSurface`).
 */

import { parseZwDocCached } from "../lib/zwdoc/format";
import { zwDocCommentRanges } from "../lib/zwdoc/comments";
import type { Node as PMNode } from "prosemirror-model";
import {
  prefixSnippet,
  rangePreviewText,
  suffixSnippet,
} from "./commentAnchors";

export type CommentAnchorSummary = {
  id: string;
  /** Commented text (or nearby prose for a point comment), for the card. */
  previewText: string;
};

/** Same wording as a Markdown point comment: prose before it, else after. */
function pointPreview(doc: PMNode, pos: number): string {
  const $pos = doc.resolve(pos);
  const block = $pos.parent;
  const offset = $pos.parentOffset;
  const before = block.textBetween(0, offset, "", " ");
  const after = block.textBetween(
    Math.min(offset + 1, block.content.size),
    block.content.size,
    "",
    " ",
  );
  const prefix = prefixSnippet(before);
  if (prefix.length > 0) return prefix;
  const suffix = suffixSnippet(after);
  return suffix.length > 0 ? suffix : "(start of line)";
}

export function commentAnchorsOf(doc: string): CommentAnchorSummary[] {
  const parsed = parseZwDocCached(doc);
  if (parsed.ok === false) return [];
  const { doc: body } = parsed.value;
  return zwDocCommentRanges(body).map((range) => ({
    id: range.id,
    previewText: range.point
      ? pointPreview(body, range.from)
      : rangePreviewText(range.text),
  }));
}

export function commentAnchorIdsOf(doc: string): string[] {
  return commentAnchorsOf(doc).map((a) => a.id);
}
